/**
 * サインイン後にサーバが認証を拒否した理由を、ログイン画面へ1回だけ引き継ぐ。
 *
 * ## なぜ必要か
 *
 * Firebaseのサインインが成功しても、サーバ側の認証interceptorが拒否する経路がある
 * （停止済みスタッフ / 有効な契約が1本も無い企業 / トークン失効）。この場合
 * `getStaffMe` が失敗し、以前は `console.error` だけで握り潰していたため、画面は
 * ログインフォームのまま何も起きなかった（2026-09の「ログインできないが何も出ない」事象）。
 *
 * 拒否を検知したらFirebaseからサインアウトし、その理由をここに預けてから
 * ログイン画面に表示する。サインアウト → `onAuthStateChanged(null)` → 画面再描画という
 * 経路をまたぐため、メモリ変数では消えてしまう。
 *
 * ## 預けるのはエラーコードだけ（サーバの文言を保存しない）
 *
 * 保存するのはアプリのエラーコード（`STAFF_ACCOUNT_DISABLED` 等）だけで、画面に出す文言は
 * 読み出し側で `getErrorMessage` から解決する。サーバが返した文字列をそのまま
 * ブラウザストレージへ書くと、何が入るかがサーバ次第になり（将来の変更で機密性のある文言が
 * 混ざりうる）、静的解析も「サーバ応答の平文保存」として検出する。
 * コードは固定の識別子なので、この懸念が構造的に消える。
 *
 * ## なぜsessionStorageか
 *
 * - localStorageにしない: 残留すると次回以降のログイン画面に古い理由が出る
 * - URLクエリ（`/login?reason=...`）にしない: 共有・ブックマークで意図せず再現し、
 *   外部から理由文言を引き出せる形にもなる
 * - 読み出しは1回限りの消費（consume）にして、リロードで消える
 *
 * ## ログイン画面を表示したまま拒否されたとき
 *
 * ログインフォームから送信して拒否された場合、ログイン画面は作り直されないので、表示時に1回だけ読む
 * 方式では理由が読まれない。そこで預けたときに同じタブへイベント（`AUTH_REJECTION_EVENT`）を出し、
 * 表示中のログイン画面は `subscribeAuthRejection` で受けて読み出す。
 * 出来事: 2026-10-13、サポートアカウント（決定125）のログイン拒否をブラウザで確かめたところ、
 * フォームから送信した拒否で理由が一度も表示されなかった（停止済みスタッフも同じ経路を通る）。
 */
import { ERROR_MESSAGES, getErrorMessage } from '@modules/error/errorCodes'

const STORAGE_KEY = 'iesapuri:auth_rejection'

/** 拒否の理由を預けたことを同じタブへ知らせるイベント名（中身は持たない。読み出しは consume で行う） */
const AUTH_REJECTION_EVENT = 'iesapuri:auth-rejection-recorded'

/**
 * `ERROR_MESSAGES` に登録が無いコードだったときの文言。
 * 拒否された事実と次の行動だけを伝える（サーバの文言に依存しない）。
 */
const FALLBACK_MESSAGE = 'ログインできませんでした。企業の管理者にお問い合わせください。'

/** 預ける内容。コードだけを持つ（文言は読み出し側で解決する） */
export interface AuthRejection {
  /** アプリのエラーコード、またはConnect code（`STAFF_ACCOUNT_DISABLED` 等） */
  code: string
}

/** 読み出し結果。`message` は `code` から解決した表示用の文言 */
export interface ResolvedAuthRejection extends AuthRejection {
  message: string
}

/** 拒否の理由を預ける。sessionStorageが使えない環境（プライベートモード等）では何もしない */
export const recordAuthRejection = (rejection: AuthRejection): void => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ code: rejection.code }))
  } catch {
    // 書き込めない環境では引き継げない。トースト等の他の経路に委ねる
    return
  }
  window.dispatchEvent(new Event(AUTH_REJECTION_EVENT))
}

/**
 * 拒否の理由が預けられたら呼ばれる関数を登録し、登録を外す関数を返す。
 * 表示中のログイン画面が、フォームからの送信で拒否されたときに理由を読み出すのに使う。
 */
export const subscribeAuthRejection = (listener: () => void): (() => void) => {
  window.addEventListener(AUTH_REJECTION_EVENT, listener)
  return () => window.removeEventListener(AUTH_REJECTION_EVENT, listener)
}

/** 預けたコードを読み出して削除し、表示用の文言を添えて返す。無ければnull */
export const consumeAuthRejection = (): ResolvedAuthRejection | null => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    sessionStorage.removeItem(STORAGE_KEY)
    if (raw === null) return null
    const parsed: unknown = JSON.parse(raw)
    if (parsed === null || typeof parsed !== 'object') return null
    const { code } = parsed as { code?: unknown }
    if (typeof code !== 'string' || code === '') return null
    return { code, message: resolveRejectionMessage(code) }
  } catch {
    return null
  }
}

/** エラーコードから画面に出す文言を決める。未登録のコードは汎用の案内にする */
export const resolveRejectionMessage = (code: string): string => {
  // 登録の有無で判定する。getErrorMessageの戻り値（未登録時は「エラーが発生しました」）と
  // 文言リテラルで比較すると、errorCodes.ts側の既定文言を変えた瞬間に静かに壊れる
  const registered = (ERROR_MESSAGES as Record<string, string>)[code] != null
  return registered ? getErrorMessage(code) : FALLBACK_MESSAGE
}

/** 預けた理由を捨てる（テストのクリーンアップ・明示ログアウト時） */
export const clearAuthRejection = (): void => {
  try {
    sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    // ignore
  }
}
